import { useState, useEffect } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const HERO_IMAGES = [
  {
    src: "/hero1.jpg",
    label: "Quinta das Cantigas",
  },
  {
    src: "https://eco.yourholiday.pt/wp-content/uploads/Glamping-Portugal-at-Quinta-das-Cantigas_red-ford.jpg",
    label: "A Rode Ford 1972",
  },
  {
    src: "https://eco.yourholiday.pt/wp-content/uploads/Kleinschalig-vakantie-park-Portugal_glamping-Tendas-Toca-1350x581.jpg",
    label: "Tendas Toca",
  },
  {
    src: "https://eco.yourholiday.pt/wp-content/uploads/Kleinschalig-vakantie-park-Portugal_glamping-Tendas-Toca_view-to-pool.jpg",
    label: "Pool View",
  },
  {
    src: "/nazare 1.jpg",
    label: "Nazaré",
  },
];

export default function Hero() {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrent((c) => (c + 1) % HERO_IMAGES.length);
        setFade(true);
      }, 400);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const goTo = (idx) => {
    setFade(false);
    setTimeout(() => {
      setCurrent(idx);
      setFade(true);
    }, 300);
  };

  const prev = () => goTo((current - 1 + HERO_IMAGES.length) % HERO_IMAGES.length);
  const next = () => goTo((current + 1) % HERO_IMAGES.length);

  return (
    <section id="home" className="relative w-full h-screen overflow-hidden" data-testid="hero-section">
      {/* Background images */}
      {HERO_IMAGES.map((img, i) => (
        <div
          key={img.src}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: i === current && fade ? 1 : i === current ? 0.3 : 0 }}
        >
          <img
            src={img.src}
            alt={img.label}
            className="w-full h-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
          />
        </div>
      ))}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 mb-6 animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-[#D4A373] animate-pulse" />
              <span className="text-white/90 text-xs font-medium uppercase tracking-[0.15em] font-manrope">
                {t("hero.badge")}
              </span>
            </div>

            <h1
              className="font-playfair text-5xl sm:text-6xl lg:text-7xl font-medium text-white leading-[1.05] mb-5"
              style={{ textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}
            >
              {t("hero.title")}
            </h1>

            <p className="text-xl sm:text-2xl font-light text-[#D4A373] font-playfair italic mb-4">
              {t("hero.subtitle")}
            </p>

            <p className="text-base md:text-lg text-white/80 font-manrope font-light leading-relaxed max-w-xl mb-10">
              {t("hero.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#accommodations"
                className="inline-flex items-center justify-center rounded-full px-8 py-4 bg-[#3A4D39] text-white font-medium hover:bg-[#2C3E2B] transition-all duration-300 hover:scale-[1.02] shadow-lg font-manrope"
                data-testid="hero-cta-accommodations"
              >
                {t("hero.cta")}
              </a>
              <a
                href="https://book.yourholiday.pt/en/search-availability/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full px-8 py-4 bg-white/15 backdrop-blur-sm border border-white/30 text-white font-medium hover:bg-white/25 transition-all duration-300 font-manrope"
                data-testid="hero-cta-book"
              >
                {t("hero.ctaBook")}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Slider controls */}
      <div className="absolute bottom-24 right-6 md:right-12 z-10 flex flex-col items-end gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={prev}
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            data-testid="hero-prev"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={next}
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            data-testid="hero-next"
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <span className="text-white/60 text-xs font-manrope">{HERO_IMAGES[current].label}</span>
      </div>

      {/* Dots */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full ${
              i === current ? "w-8 h-2 bg-white" : "w-2 h-2 bg-white/40"
            }`}
            data-testid={`hero-dot-${i}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <a
        href="#about"
        className="absolute bottom-8 left-6 md:left-12 z-10 flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors"
        data-testid="hero-scroll"
      >
        <span className="text-xs uppercase tracking-widest font-manrope">{t("hero.scroll")}</span>
        <ChevronDown className="w-4 h-4 animate-bounce" />
      </a>
    </section>
  );
}
